import React, { forwardRef, useCallback, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import { CalendarDate } from '@internationalized/date';

import { getUserFormat, useUserFormatsVersion } from '../../common/user-formats/userFormats.js';

import 'react-datepicker/dist/react-datepicker.css';
import './AppDatePicker.css';

/**
 * Map the user's date format (shared user formats) to date-fns format tokens.
 * react-datepicker uses date-fns format strings internally.
 *
 * Angular format → date-fns format:
 *   d MMM, yyyy  → d MMM, yyyy     (1 Jan, 2026)
 *   d MMMM yyyy  → d MMMM yyyy     (1 January 2026)
 *   dd MMMM yyyy → dd MMMM yyyy    (01 January 2026)
 *   MM/dd/yyyy   → MM/dd/yyyy      (01/15/2026)
 *   MMMM d, yyyy → MMMM d, yyyy    (January 1, 2026)
 *   dd/MM/yyyy   → dd/MM/yyyy      (15/01/2026)
 *   dd.MM.yyyy   → dd.MM.yyyy      (15.01.2026)
 *   yyyy-MM-dd   → yyyy-MM-dd      (2026-01-15)
 *
 * date-fns tokens match Angular tokens for the formats we use.
 */
function getUserDateFormat() {
	const fmt = getUserFormat('angularDateFormat');
	if (fmt && typeof fmt === 'string') return fmt;
	return 'd MMMM yyyy'; // default: "1 January 2026"
}

/**
 * Convert @internationalized/date CalendarDate or CalendarDateTime to JS Date.
 * CalendarDateTime has hour/minute/second fields; CalendarDate only has year/month/day.
 */
function toJSDate(calDate) {
	if (!calDate) return null;
	if (calDate instanceof Date) return calDate;
	if (calDate.year != null && calDate.month != null && calDate.day != null) {
		const hour = calDate.hour ?? 0;
		const minute = calDate.minute ?? 0;
		const second = calDate.second ?? 0;
		return new Date(calDate.year, calDate.month - 1, calDate.day, hour, minute, second);
	}
	// Try string parse as fallback
	const str = String(calDate);
	const d = new Date(str.replace(/-/g, '/'));
	return isNaN(d.getTime()) ? null : d;
}

/**
 * Convert JS Date to CalendarDate (date-only, no time).
 */
function toCalendarDate(date) {
	if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;
	return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

/**
 * Convert JS Date to a CalendarDateTime-like plain object (with time).
 * We return a plain object with the same shape that @internationalized/date
 * CalendarDateTime uses, so consumers that read .hour/.minute still work.
 */
function toCalendarDateTime(date) {
	if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;
	return {
		year: date.getFullYear(),
		month: date.getMonth() + 1,
		day: date.getDate(),
		hour: date.getHours(),
		minute: date.getMinutes(),
		second: date.getSeconds(),
		millisecond: 0,
		toDate() { return new Date(date); },
		toString() {
			const pad = n => String(n).padStart(2, '0');
			return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
		}
	};
}

/**
 * Custom input for labelPlacement="inside" — shows floating label + value,
 * matching HeroUI Select sm appearance.
 */
const InsideInput = forwardRef(({ value, onClick, placeholder, disabled, isInvalid }, ref) => (
	<button
		type="button"
		ref={ref}
		onClick={onClick}
		disabled={disabled}
		className={`app-datepicker-input app-datepicker-input--inside ${isInvalid ? 'app-datepicker-input--invalid' : ''}`}
	>
		{value ? (
			<>
				<span className="app-datepicker-inside-label">{placeholder}</span>
				<span className="app-datepicker-inside-value">{value}</span>
			</>
		) : (
			<span className="app-datepicker-inside-placeholder">{placeholder}</span>
		)}
	</button>
));

/**
 * AppDatePicker — wrapper around react-datepicker styled to match HeroUI design.
 *
 * Props API:
 *   - value: CalendarDate | CalendarDateTime | Date | string | null
 *   - onChange: (CalendarDate | CalendarDateTime-like | null) => void
 *   - label: string
 *   - isRequired: boolean
 *   - isDisabled: boolean
 *   - granularity: 'day' (default) | 'minute' | 'hour' — when 'minute'/'hour', shows time picker
 *   - hourCycle: 'h23' | 'h12' — 24h or 12h format (default 'h23')
 *   - isInvalid: boolean — error state
 *   - errorMessage: string
 *
 * Ignored HeroUI-specific props (passed through via ...rest but harmless):
 *   - showMonthAndYearPickers, placeholderValue, hideTimeZone
 */
export function AppDatePicker({
	value,
	onChange,
	label,
	labelPlacement = 'outside',
	isRequired = false,
	isDisabled = false,
	granularity = 'day',
	hourCycle = 'h23',
	isInvalid = false,
	errorMessage,
	className,
	// Strip HeroUI-only props so they don't hit the DOM
	showMonthAndYearPickers,
	placeholderValue,
	hideTimeZone,
	size,
	...rest
}) {
	const showTime = granularity === 'minute' || granularity === 'hour';
	const timeFormat = hourCycle === 'h12' ? 'h:mm aa' : 'HH:mm';
	const formatsVersion = useUserFormatsVersion();
	const dateFormat = useMemo(() => {
		const base = getUserDateFormat();
		return showTime ? `${base} ${timeFormat}` : base;
	}, [showTime, timeFormat, formatsVersion]);

	const selected = useMemo(() => toJSDate(value), [value]);

	const handleChange = useCallback(
		date => {
			if (typeof onChange === 'function') {
				onChange(showTime ? toCalendarDateTime(date) : toCalendarDate(date));
			}
		},
		[onChange, showTime]
	);

	const isInside = labelPlacement === 'inside';
	const placeholderText = isInside && label
		? label
		: (showTime ? 'Select date & time' : 'Select a date');

	return (
		<div className={`app-datepicker-wrapper ${className || ''}`}>
			{label && !isInside && (
				<label className="app-datepicker-label">
					{label}
					{isRequired && <span className="app-datepicker-required">*</span>}
				</label>
			)}
			<DatePicker
				selected={selected}
				onChange={handleChange}
				dateFormat={dateFormat}
				disabled={isDisabled}
				placeholderText={placeholderText}
				showMonthDropdown
				showYearDropdown
				dropdownMode="select"
				todayButton="Today"
				showPopperArrow={false}
				popperProps={{ strategy: 'fixed' }}
				fixedHeight
				showTimeSelect={showTime}
				timeFormat={timeFormat}
				timeIntervals={granularity === 'hour' ? 60 : 15}
				className={isInside ? undefined : `app-datepicker-input ${isInvalid ? 'app-datepicker-input--invalid' : ''}`}
				calendarClassName="app-datepicker-calendar"
				wrapperClassName="app-datepicker-input-wrapper"
				popperClassName="app-datepicker-popper"
				{...(isInside ? { customInput: <InsideInput isInvalid={isInvalid} /> } : {})}
				{...rest}
			/>
			{isInvalid && errorMessage && (
				<p className="app-datepicker-error">{errorMessage}</p>
			)}
		</div>
	);
}
